import React, { useState, useEffect, useRef } from 'react';
import { Map, ExternalLink, X, Loader, Search } from 'lucide-react';
import { globalWidgetTextStyles, widgetClassName } from '../../styles/globalWidgetStyles';
import { Token } from '../../types/supabase';
import TokenSearchModal from '../../components/TokenSearchModal';

// Define the props for the EKKOmap component
interface EKKOmapProps {
  onClose?: () => void;
  isWidget?: boolean;
}

// Custom styles for the EKKOmap widget - using white theme
const customStyles = `
  .ekko-map-widget .map-iframe {
    border: none;
    width: 100%;
    height: 100%;
    border-radius: 0 0 0.75rem 0.75rem;
    display: block;
    overflow: hidden;
    position: relative;
    z-index: 1;
    contain: layout style size;
    isolation: isolate;
  }

  .ekko-map-widget .loading-container {
    background: white;
    color: #24c35b;
  }

  .ekko-map-widget .error-container {
    background: white;
    color: #ef4444;
  }

  .ekko-map-widget .logo-container {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .ekko-map-widget .faster100x-logo {
    height: 20px;
    width: auto;
    filter: brightness(0) saturate(100%) invert(73%) sepia(44%) saturate(1458%) hue-rotate(94deg) brightness(85%) contrast(89%);
  }
`;

const EKKOmap: React.FC<EKKOmapProps> = ({
  onClose,
  isWidget = false
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [selectedToken, setSelectedToken] = useState<Token | null>(null);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const searchButtonRef = useRef<HTMLButtonElement>(null);

  // Default token data for the iframe URL
  const defaultTokenChain = 'sol';
  const defaultTokenAddress = '6p6xgHyF7AeE6TZkSmFsko444wqoP15icUSqi2jfGiPN';
  const defaultPlatform = 'ekko';

  // Get the current token address to use (selected token or default)
  const currentTokenAddress = selectedToken?.mint || defaultTokenAddress;

  // Construct the iframe URL with dynamic token address
  const iframeUrl = `https://faster100x.com/en/lite/embedded?tokenChain=${defaultTokenChain}&tokenAddress=${currentTokenAddress}&platform=${defaultPlatform}`;

  // Handle token selection from search modal
  const handleTokenSelect = (token: Token) => {
    setSelectedToken(token);
    setIsLoading(true); // Reset loading state when token changes
    setHasError(false);
  };

  // Handle iframe load events
  const handleIframeLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // Open in new window
  const openInNewWindow = () => {
    window.open(iframeUrl, '_blank', 'noopener,noreferrer');
  };

  const renderContent = () => {
    if (hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full error-container">
          <Map size={48} className="mb-4 text-gray-400" />
          <h3 className="text-lg mb-2 text-black">Failed to Load Map</h3>
          <p className="text-gray-600 text-center mb-4">
            Unable to load the Faster100x map. Please check your connection.
          </p>
          <button
            onClick={openInNewWindow}
            className="px-4 py-2 bg-[#24c35b]/20 hover:bg-[#24c35b]/30 text-[#24c35b] rounded-lg transition-colors flex items-center gap-2"
          >
            <ExternalLink size={16} />
            Open in New Window
          </button>
        </div>
      );
    }

    return (
      <div className="relative w-full h-full" style={{ contain: 'layout style size', isolation: 'isolate' }}>
        {isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center loading-container z-10">
            <Loader size={48} className="mb-4 animate-spin" />
            <p className="text-gray-600">Loading Faster100x Map...</p>
          </div>
        )}
        <iframe
          src={iframeUrl}
          className="map-iframe"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          title="Faster100x Token Map"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        />
      </div>
    );
  };

  const content = (
    <div className={`w-full h-full flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm ${widgetClassName} ekko-map-widget`} data-widget-type="ekko-map">
      <style>{globalWidgetTextStyles}</style>
      <style>{customStyles}</style>
      
      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between rounded-t-xl">
        <div className="flex items-center gap-2 logo-container">
          <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
          <Map size={18} className="text-[#24c35b]" />
          <h2 className="text-lg font-bold text-black">EKKOmap</h2>
          <img 
            src="https://faster100x.com/faster100x-text.svg" 
            alt="Faster100x" 
            className="faster100x-logo"
            onError={(e) => {
              // Hide the logo if it fails to load
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            ref={searchButtonRef}
            onClick={() => setIsSearchModalOpen(true)}
            className="p-2 rounded-lg bg-[#24c35b]/10 hover:bg-[#24c35b]/20 text-[#24c35b] transition-colors"
            title="Search for a token"
          >
            <Search size={14} />
          </button>
          
          <button
            onClick={openInNewWindow}
            className="p-2 rounded-lg bg-[#24c35b]/10 hover:bg-[#24c35b]/20 text-[#24c35b] transition-colors"
            title="Open in new window"
          >
            <ExternalLink size={14} />
          </button>
          
          {onClose && (
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-black transition-colors p-1 rounded hover:bg-gray-100"
              title="Close"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>
      
      {/* Token Info Bar - Show selected token if available */}
      {selectedToken && (
        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {selectedToken.image_url && (
              <img 
                src={selectedToken.image_url} 
                alt={selectedToken.symbol || 'Token'} 
                className="w-5 h-5 rounded-full"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://via.placeholder.com/20/24c35b/FFFFFF/?text=' + (selectedToken.symbol?.charAt(0) || 'T');
                }}
              />
            )}
            <div>
              <span className="text-sm font-medium text-black">
                {selectedToken.symbol || 'Unknown Token'}
              </span>
              <span className="text-xs text-gray-600 ml-2">
                {selectedToken.name || 'No name available'}
              </span>
            </div>
          </div>
          <button
            onClick={() => setSelectedToken(null)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Clear token selection"
          >
            <X size={14} />
          </button>
        </div>
      )}
      
      {/* Content */}
      <div className="flex-grow overflow-hidden bg-white">
        {renderContent()}
      </div>
      
      {/* Token Search Modal */}
      <TokenSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onSelectToken={handleTokenSelect}
        referenceElement={searchButtonRef.current}
      />
    </div>
  );

  // If this is being rendered as a widget, return just the content
  if (isWidget) {
    return content;
  }

  // Otherwise, return the modal version with container
  return (
    <div className="p-4 bg-dark-grey border border-gray-800 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 logo-container">
          <Map size={18} className="text-primary" />
          <h2 className="text-lg font-bold text-white">EKKOmap</h2>
          <img 
            src="https://faster100x.com/faster100x-text.svg" 
            alt="Faster100x" 
            className="faster100x-logo"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            &times;
          </button>
        )}
      </div>
      
      {content}
    </div>
  );
};

export default EKKOmap;

/**
 * Factory function to create an EKKOmap widget
 * @param props - The properties to initialize the EKKOmap component with
 * @returns An EKKOmap widget object with content and metadata
 */
export function createEKKOmapWidget(props?: EKKOmapProps) {
  const uniqueKey = `ekko-map-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  
  return {
    type: 'ekko-map-widget',
    title: 'EKKOmap',
    content: <EKKOmap 
      key={uniqueKey}
      {...props} 
      isWidget={true} 
    />,
    size: { width: 800, height: 600 },
    contentStyle: { 
      padding: 0,
      overflow: 'hidden' 
    },
    glassMorphism: true,
    containerStyle: {
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(12px)",
      borderRadius: "12px",
      border: "1px solid rgba(36, 195, 91, 0.2)",
      boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(36, 195, 91, 0.2)",
      overflow: "hidden",
    }
  };
} 